import React, { useState, useEffect } from 'react';
import { keyFeatures } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const KeyFeatures = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useLanguage();
  const keyFeaturesText = t('keyFeatures');

  // Use translated features if available, otherwise use mock data
  const features = Array.isArray(keyFeaturesText?.features) ? keyFeaturesText.features : keyFeatures;
  const featuresLength = features?.length || 1;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % featuresLength);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuresLength]);

  return (
    <section className="bg-[#0a0a0b] py-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-400/10 text-teal-400 text-sm font-medium px-4 py-1.5 rounded-full">
            {keyFeaturesText?.title || 'Key Features'}
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mt-6">
            {keyFeaturesText?.subtitle || 'Build high quality Voice AI assistants in minutes'}
          </h2>
        </div>

        {/* Features Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left Side - Feature List */}
          <div className="space-y-6">
            {Array.isArray(features) && features.map((feature, index) => (
              <div
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`p-6 rounded-xl cursor-pointer transition-all duration-300 border ${
                  activeIndex === index
                    ? 'bg-[#1a1a1f] border-teal-400/30'
                    : 'bg-transparent border-transparent hover:bg-[#1a1a1f]/50'
                }`}
              >
                <h3 className={`text-xl font-semibold mb-2 transition-colors ${
                  activeIndex === index ? 'text-white' : 'text-gray-400'
                }`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed transition-colors ${
                  activeIndex === index ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Right Side - Image */}
          <div className="relative">
            <div className="bg-[#1a1a1f] rounded-2xl overflow-hidden border border-gray-800 shadow-2xl">
              <div className="aspect-video relative">
                <img
                  src={keyFeatures[activeIndex].image}
                  alt={keyFeatures[activeIndex].title}
                  className="w-full h-full object-cover transition-opacity duration-500"
                />
              </div>
            </div>
            {/* Decorative Elements */}
            <div className="absolute -z-10 top-10 right-10 w-full h-full bg-teal-400/5 rounded-2xl" />
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center gap-2 mt-10">
          {keyFeatures.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeIndex === index ? 'w-8 bg-teal-400' : 'w-2 bg-gray-700 hover:bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyFeatures;
